import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, ROLES, ROLE_LABELS, SHIFTS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, Warning, ShieldCheck, Copy, CaretRight, Eye,
} from "@phosphor-icons/react";
import EmployeeProfileDialog from "@/components/EmployeeProfileDialog";

const EMPLOYMENT_TYPES = ["FULL_TIME", "PART_TIME", "CONTRACT", "INTERN", "CONSULTANT"];
const CURRENT_STATUSES = ["ACTIVE", "ON_LEAVE", "SUSPENDED", "RESIGNED", "TERMINATED"];

const EMPTY = {
  employee_id: "", email: "", name: "", password: "Welcome@123",
  role: "collection_staff", designation: "", department: "",
  shift: "OFFICE", contact: "", emergency_contact: "",
  bank_account: "", upi_id: "", monthly_salary: 0,
  monthly_working_days: 26, daily_working_hours: 8,
  reporting_manager: "",
  // Personal
  gender: "", date_of_birth: "", nationality: "Indian",
  address: "", city: "", state: "", country: "India", pin_code: "",
  // Identity
  aadhaar: "", pan: "", driving_license: "", passport: "", voter_id: "",
  // Bank
  bank_name: "", account_holder: "", ifsc: "", branch: "",
  // Employment
  employment_type: "FULL_TIME", current_status: "ACTIVE",
  // Payroll calc defaults
  salary_calc_method: "FULL_DAY",
  daily_assigned_jobs: 0, min_required_jobs: 0,
  perf_deduction_per_missing_job: 0, perf_bonus_per_extra_job: 0,
};

export default function Employees() {
  const { user: me } = useAuth();
  const isSA = me?.role === "super_admin";
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [delOpen, setDelOpen] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [tab, setTab] = useState("basic");
  // Employee profile viewer
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileEmpId, setProfileEmpId] = useState(null);
  const isAdminViewer = ["super_admin", "managing_director", "hr_manager"].includes(me?.role);

  async function load() {
    try { const { data } = await api.get("/employees"); setList(data); } catch (e) { toast.error(formatApiError(e)); }
  }
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/employees"); setList(data); }
      catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  function openCreate() { setEditing(null); setForm(EMPTY); setOpen(true); setTab("basic"); }
  function handleEditClick(empRow) {
    setEditing(empRow);
    setForm({ ...EMPTY, ...empRow, password: "" });
    setOpen(true);
    setTab("basic");
  }

  async function onSave() {
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.employee_id; delete payload.email;
        // strip out fields not in update model
        ["verification_id","verification_status","employment_history",
         "code_of_conduct","created_at","id","password_hash"].forEach(k=>delete payload[k]);
        await api.patch(`/employees/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", form);
        toast.success("Employee added");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function onDelete(emp) {
    if (!window.confirm(`Deactivate ${emp.name}?`)) return;
    try { await api.delete(`/employees/${emp.id}`); load(); toast.success("Deactivated"); }
    catch (e) { toast.error(formatApiError(e)); }
  }

  function toggleSelect(id) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleSelectAll(ids) {
    setSelected((s) => s.size === ids.length ? new Set() : new Set(ids));
  }
  async function confirmBulkDelete() {
    if (!delPw) { toast.error("Enter your password to confirm"); return; }
    setDelBusy(true);
    try {
      const { data } = await api.post("/employees/bulk-delete", {
        ids: Array.from(selected), password: delPw,
      });
      toast.success(`${data.deleted} employee(s) permanently deleted`);
      setSelected(new Set()); setDelPw(""); setDelOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDelBusy(false); }
  }

  function copyVid(vid) {
    if (!vid) return;
    navigator.clipboard?.writeText(vid);
    toast.success(`Copied ${vid}`);
  }

  const filtered = list.filter((e) =>
    !q || e.name?.toLowerCase().includes(q.toLowerCase())
       || e.employee_id?.toLowerCase().includes(q.toLowerCase())
       || e.email?.toLowerCase().includes(q.toLowerCase())
       || e.verification_id?.toLowerCase().includes(q.toLowerCase())
  );

  const set = (k) => (v) => setForm({ ...form, [k]: v });

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E50914] mb-2">Workforce</p>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight">Employees</h1>
        </div>
        <div className="flex gap-3">
          <Input
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, ID or ETH-IND-…"
            className="w-72 bg-[#16161C] border-[#2D2D35] h-10"
            data-testid="employee-search"
          />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} data-testid="add-employee-button"
                      className="bg-[#E50914] hover:bg-[#C80812] h-10">
                <Plus size={16} weight="bold" className="mr-1" /> Add employee
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#16161C] border-[#2D2D35] text-white max-w-3xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {editing ? `Edit · ${editing.name}` : "Add Employee"}
                  {editing?.verification_id && (
                    <span className="ml-auto text-[10px] font-mono px-2 py-1 rounded bg-[#0D0D10] border border-[#E50914]/40 text-[#E50914]"
                          data-testid="edit-vid-badge">
                      {editing.verification_id}
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>

              <Tabs value={tab} onValueChange={setTab} className="mt-3">
                <TabsList className="bg-[#0D0D10] border border-[#2D2D35] w-full justify-start overflow-x-auto"
                          data-testid="emp-form-tabs">
                  <TabsTrigger value="basic" data-testid="tab-basic">Basic</TabsTrigger>
                  <TabsTrigger value="personal" data-testid="tab-personal">Personal</TabsTrigger>
                  <TabsTrigger value="identity" data-testid="tab-identity">Identity</TabsTrigger>
                  <TabsTrigger value="bank" data-testid="tab-bank">Bank</TabsTrigger>
                  <TabsTrigger value="employment" data-testid="tab-employment">Employment</TabsTrigger>
                </TabsList>

                {/* BASIC */}
                <TabsContent value="basic" className="mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    {!editing && (<>
                      <Field label="Employee ID" value={form.employee_id} onChange={set("employee_id")} testid="emp-code-input" />
                      <Field label="Email" value={form.email} onChange={set("email")} testid="emp-email-input" />
                    </>)}
                    <Field label="Full name" value={form.name} onChange={set("name")} testid="emp-name-input" />
                    <Field label="Designation" value={form.designation} onChange={set("designation")} />
                    <Field label="Department" value={form.department} onChange={set("department")} />
                    <Field label="Contact" value={form.contact} onChange={set("contact")} />
                    <Field label="Emergency contact" value={form.emergency_contact} onChange={set("emergency_contact")} />
                    <Field label="Monthly Working Days" type="number" value={form.monthly_working_days} onChange={(v)=>set("monthly_working_days")(Number(v))} testid="emp-mwd-input" />
                    <Field label="Daily Working Hours" type="number" value={form.daily_working_hours} onChange={(v)=>set("daily_working_hours")(Number(v))} testid="emp-dwh-input" />
                    <Field label="Monthly Salary (INR)" type="number" value={form.monthly_salary} onChange={(v)=>set("monthly_salary")(Number(v))} testid="emp-salary-input" />
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-zinc-500">Per Hour Pay (auto)</Label>
                      <Input readOnly value={`₹ ${(() => {
                        const mwd = Number(form.monthly_working_days) || 26;
                        const dwh = Number(form.daily_working_hours) || 8;
                        const ms = Number(form.monthly_salary) || 0;
                        return dwh && mwd ? ((ms / mwd) / dwh).toFixed(2) : "0.00";
                      })()} / hr · ₹ ${(() => {
                        const mwd = Number(form.monthly_working_days) || 26;
                        const ms = Number(form.monthly_salary) || 0;
                        return mwd ? (ms / mwd).toFixed(2) : "0.00";
                      })()} / day`}
                      data-testid="emp-hourly-readonly"
                      className="mt-2 bg-[#0D0D10] border-[#2D2D35] text-[#E50914] font-mono cursor-not-allowed" />
                    </div>
                    <Field label="Reporting manager" value={form.reporting_manager} onChange={set("reporting_manager")} />
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-zinc-500">Role</Label>
                      <Select value={form.role} onValueChange={set("role")}>
                        <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="emp-role-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                          {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-zinc-500">Shift</Label>
                      <Select value={form.shift} onValueChange={set("shift")}>
                        <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="emp-shift-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                          {SHIFTS.map((s) => <SelectItem key={s} value={s}>{s.replace("_"," ")}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Field label={editing ? "New password (leave empty to keep)" : "Password"}
                           value={form.password} onChange={set("password")} />
                  </div>
                </TabsContent>

                {/* PERSONAL */}
                <TabsContent value="personal" className="mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-zinc-500">Gender</Label>
                      <Select value={form.gender || ""} onValueChange={set("gender")}>
                        <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="emp-gender-select"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                          {["MALE","FEMALE","OTHER","PREFER_NOT_TO_SAY"].map((g)=>(
                            <SelectItem key={g} value={g}>{g.replaceAll("_"," ")}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Field label="Date of Birth" type="date" value={(form.date_of_birth||"").slice(0,10)} onChange={set("date_of_birth")} testid="emp-dob-input" />
                    <Field label="Nationality" value={form.nationality} onChange={set("nationality")} />
                    <Field label="Pin Code" value={form.pin_code} onChange={set("pin_code")} />
                    <div className="col-span-2">
                      <Field label="Address" value={form.address} onChange={set("address")} testid="emp-address-input" />
                    </div>
                    <Field label="City" value={form.city} onChange={set("city")} />
                    <Field label="State" value={form.state} onChange={set("state")} />
                    <Field label="Country" value={form.country} onChange={set("country")} />
                  </div>
                </TabsContent>

                {/* IDENTITY */}
                <TabsContent value="identity" className="mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Aadhaar Number" value={form.aadhaar} onChange={set("aadhaar")} testid="emp-aadhaar-input" />
                    <Field label="PAN" value={form.pan} onChange={(v)=>set("pan")(String(v||"").toUpperCase())} testid="emp-pan-input" />
                    <Field label="Driving License" value={form.driving_license} onChange={set("driving_license")} />
                    <Field label="Passport No." value={form.passport} onChange={set("passport")} />
                    <Field label="Voter ID" value={form.voter_id} onChange={set("voter_id")} />
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-4 leading-relaxed">
                    Identity documents are <b className="text-zinc-300">private</b>: never returned by the public verification API.
                  </div>
                </TabsContent>

                {/* BANK */}
                <TabsContent value="bank" className="mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Bank Name" value={form.bank_name} onChange={set("bank_name")} testid="emp-bankname-input" />
                    <Field label="Account Holder" value={form.account_holder} onChange={set("account_holder")} />
                    <Field label="Bank Account Number" value={form.bank_account} onChange={set("bank_account")} testid="emp-bankacc-input" />
                    <Field label="IFSC" value={form.ifsc} onChange={(v)=>set("ifsc")(String(v||"").toUpperCase())} testid="emp-ifsc-input" />
                    <Field label="Branch" value={form.branch} onChange={set("branch")} />
                    <Field label="UPI ID" value={form.upi_id} onChange={set("upi_id")} />
                  </div>
                </TabsContent>

                {/* EMPLOYMENT */}
                <TabsContent value="employment" className="mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-zinc-500">Employment Type</Label>
                      <Select value={form.employment_type || "FULL_TIME"} onValueChange={set("employment_type")}>
                        <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="emp-emptype-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                          {EMPLOYMENT_TYPES.map((t)=> <SelectItem key={t} value={t}>{t.replaceAll("_"," ")}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-widest text-zinc-500">Current Status</Label>
                      <Select value={form.current_status || "ACTIVE"} onValueChange={set("current_status")}>
                        <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="emp-empstatus-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                          {CURRENT_STATUSES.map((s)=> <SelectItem key={s} value={s}>{s.replaceAll("_"," ")}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Field label="Joining Date" type="date" value={(form.joining_date||"").slice(0,10)} onChange={set("joining_date")} />
                    {editing && (
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-zinc-500">Verification Status</Label>
                        <Select value={form.verification_status || "PENDING"} onValueChange={set("verification_status")}>
                          <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="emp-verifstatus-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                            {["PENDING","VERIFIED","NOT_VERIFIED"].map((s)=>(
                              <SelectItem key={s} value={s}>{s.replaceAll("_"," ")}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                  {editing?.verification_id && (
                    <div className="mt-5 p-4 bg-[#0D0D10] border border-[#2D2D35] rounded">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5">
                        <ShieldCheck size={12} className="text-[#E50914]" /> Global Verification ID
                      </div>
                      <div className="flex items-center gap-3">
                        <code className="font-mono text-[#E50914]">{editing.verification_id}</code>
                        <button onClick={()=>copyVid(editing.verification_id)} type="button"
                                className="p-1.5 rounded hover:bg-[#16161C]" title="Copy">
                          <Copy size={14} />
                        </button>
                        <Link to={`/verify/${editing.verification_id}`}
                              className="ml-auto text-xs text-[#E50914] hover:underline inline-flex items-center">
                          Open verification profile <CaretRight size={12} />
                        </Link>
                      </div>
                    </div>
                  )}

                  {/* Payroll defaults (Phase 7) */}
                  <div className="mt-6 pt-5 border-t border-[#2D2D35]">
                    <div className="text-[10px] uppercase tracking-widest text-[#E50914] mb-3">
                      Payroll Calculation Defaults
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-zinc-500">Salary Calculation Method</Label>
                        <Select value={form.salary_calc_method || "FULL_DAY"} onValueChange={set("salary_calc_method")}>
                          <SelectTrigger className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="emp-salary-method-select"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#16161C] border-[#2D2D35] text-white">
                            <SelectItem value="FULL_DAY">Full Day Salary</SelectItem>
                            <SelectItem value="WORKED_HOURS">Worked Hours Salary</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Field label="Daily Assigned Jobs" type="number" value={form.daily_assigned_jobs}
                             onChange={(v)=>set("daily_assigned_jobs")(Number(v)||0)} testid="emp-daily-jobs" />
                      <Field label="Minimum Required Jobs" type="number" value={form.min_required_jobs}
                             onChange={(v)=>set("min_required_jobs")(Number(v)||0)} testid="emp-min-jobs" />
                      <Field label="Perf. Deduction / Missing Job (₹)" type="number" value={form.perf_deduction_per_missing_job}
                             onChange={(v)=>set("perf_deduction_per_missing_job")(Number(v)||0)} testid="emp-perf-ded" />
                      <Field label="Perf. Bonus / Extra Job (₹)" type="number" value={form.perf_bonus_per_extra_job}
                             onChange={(v)=>set("perf_bonus_per_extra_job")(Number(v)||0)} testid="emp-perf-bon" />
                    </div>
                  </div>
                </TabsContent>
              </Tabs>

              <div className="mt-6 flex justify-end gap-3">
                <Button variant="outline" onClick={()=>setOpen(false)} className="border-[#2D2D35] bg-transparent">Cancel</Button>
                <Button onClick={onSave} data-testid="emp-save-button" className="bg-[#E50914] hover:bg-[#C80812]">
                  {editing ? "Save changes" : "Create"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Bulk-delete toolbar (super-admin only) */}
      {isSA && selected.size > 0 && (
        <div className="mb-3 flex items-center justify-between bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] px-4 py-2.5 rounded-md"
             data-testid="bulk-delete-toolbar">
          <span className="text-xs uppercase tracking-widest">{selected.size} selected</span>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected(new Set())}
                    className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white">Clear</button>
            <Button size="sm" onClick={() => setDelOpen(true)} data-testid="bulk-delete-button"
                    className="bg-[#FF3B30] hover:bg-[#E12B22] text-white">
              <Trash size={14} className="mr-1" /> Delete selected
            </Button>
          </div>
        </div>
      )}

      <div className="bg-[#16161C] border border-[#2D2D35] rounded-md overflow-hidden">
        <table className="w-full" data-testid="employees-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[#2D2D35]">
              {isSA && (
                <th className="p-3 w-10">
                  <input type="checkbox"
                         data-testid="select-all-employees"
                         checked={selected.size > 0 && selected.size === filtered.length}
                         onChange={() => toggleSelectAll(filtered.map((e) => e.id))}
                         className="h-4 w-4 accent-[#E50914]" />
                </th>
              )}
              <th className="text-left p-3">Emp ID</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Shift</th>
              <th className="text-left p-3">Verif. ID</th>
              <th className="text-right p-3">Salary</th>
              <th className="text-right p-3">Status</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e) => (
              <tr key={e.id} className={`border-b border-[#2D2D35]/60 hover:bg-[#1C1C24] ${selected.has(e.id) ? "bg-[#E50914]/5" : ""}`}>
                {isSA && (
                  <td className="p-3">
                    <input type="checkbox"
                           data-testid={`select-${e.employee_id}`}
                           checked={selected.has(e.id)}
                           onChange={() => toggleSelect(e.id)}
                           className="h-4 w-4 accent-[#E50914]" />
                  </td>
                )}
                <td className="p-3 font-mono text-xs">{e.employee_id}</td>
                <td className="p-3">
                  <div>{e.name}</div>
                  <div className="text-[10px] text-zinc-500">{e.email}</div>
                </td>
                <td className="p-3 text-xs"><span className="px-2 py-1 bg-[#0D0D10] border border-[#2D2D35] rounded">{ROLE_LABELS[e.role] || e.role}</span></td>
                <td className="p-3 text-xs text-zinc-300">{e.shift}</td>
                <td className="p-3">
                  {e.verification_id ? (
                    <Link to={`/verify/${e.verification_id}`}
                          data-testid={`vid-link-${e.employee_id}`}
                          className="font-mono text-[10px] text-[#E50914] hover:underline inline-flex items-center gap-1">
                      <ShieldCheck size={11} weight="fill" /> {e.verification_id}
                    </Link>
                  ) : <span className="text-[10px] text-zinc-500">—</span>}
                </td>
                <td className="p-3 text-right font-mono text-sm">{Number(e.monthly_salary||0).toLocaleString()}</td>
                <td className="p-3 text-right">
                  <span className={`text-[10px] px-2 py-1 rounded ${e.active ? "bg-[#22C55E]/15 text-[#22C55E]" : "bg-zinc-700/30 text-zinc-400"}`}>
                    {e.active ? "ACTIVE" : "INACTIVE"}
                  </span>
                </td>
                <td className="p-3 text-right space-x-1">
                  <button onClick={()=>{ setProfileEmpId(e.id); setProfileOpen(true); }}
                          className="p-2 hover:text-[#22C55E]"
                          title="View profile"
                          data-testid={`view-${e.employee_id}`}>
                    <Eye size={16} />
                  </button>
                  <button onClick={()=>handleEditClick(e)} className="p-2 hover:text-[#E50914]" data-testid={`edit-${e.employee_id}`}><PencilSimple size={16} /></button>
                  {!isSA && (
                    <button onClick={()=>onDelete(e)} title="Deactivate" className="p-2 hover:text-[#FF3B30]"><Trash size={16} /></button>
                  )}
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={isSA ? 9 : 8} className="text-center text-zinc-500 p-8 text-sm">No employees match.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Password-verify delete dialog */}
      <Dialog open={delOpen} onOpenChange={(v) => { if (!delBusy) setDelOpen(v); }}>        <DialogContent className="bg-[#16161C] border-[#FF3B30]/40 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#FF3B30]">
              <Warning size={18} weight="fill" /> Permanently delete {selected.size} employee(s)
            </DialogTitle>
          </DialogHeader>
          <div className="text-xs text-zinc-400 mt-2 leading-relaxed">
            This action is <b>irreversible</b>. Attendance, leaves, fines, incentives, advances and payslips for these employees will also be removed.
            Super-admin and your own account are protected.
          </div>
          <div className="mt-4">
            <Label className="text-xs uppercase tracking-widest text-zinc-500">Confirm with your password</Label>
            <Input type="password" value={delPw} onChange={(e)=>setDelPw(e.target.value)}
                   placeholder="Enter password"
                   className="mt-2 bg-[#0D0D10] border-[#2D2D35]" data-testid="bulk-delete-password"
                   autoComplete="current-password" />
          </div>
          <div className="mt-5 flex justify-end gap-3">
            <Button variant="outline" disabled={delBusy} onClick={()=>{ setDelOpen(false); setDelPw(""); }}
                    className="border-[#2D2D35] bg-transparent">Cancel</Button>
            <Button onClick={confirmBulkDelete} disabled={delBusy} data-testid="bulk-delete-confirm"
                    className="bg-[#FF3B30] hover:bg-[#E12B22] text-white">
              {delBusy ? "Deleting…" : `Delete ${selected.size}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <EmployeeProfileDialog
        open={profileOpen}
        onOpenChange={setProfileOpen}
        employeeId={profileEmpId}
        isAdmin={isAdminViewer}
      />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} type={type} data-testid={testid}
             className="mt-2 bg-[#0D0D10] border-[#2D2D35] focus:border-[#E50914] focus:ring-1 focus:ring-[#E50914]" />
    </div>
  );
}
